import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { ENVIRONMENT } from '@ld.consumer-portal/models';
import { firstValueFrom, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { MlaAuthService } from './mla-auth.service';
import {
  MlaFeatureFlagResponse,
  MlaFeatureFlagKey,
  MlaB2CConfigSettings,
  MlaConfigSettingResponse,
  MlaEntraSettings,
} from '@ld.consumer-portal/models';
import { LoggingService } from '@ld.consumer-portal/utils';

@Injectable({
  providedIn: 'root',
})
export class MlaConfigService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly authService = inject(MlaAuthService);
  private readonly loggingService = inject(LoggingService);

  private readonly featureFlagCache = new Map<string, boolean>();

  readonly b2cConfig = signal<MlaB2CConfigSettings | null>(null);
  readonly isB2CEnabled = signal(false);
  readonly homePageUrl = signal<string | null>(null);
  readonly entraSettings = signal<MlaEntraSettings | null>(null);

  private get mlaApiUrl(): string {
    return this.env.appSettings.mlaApiUrl || '';
  }

  async getFeatureFlag(key: MlaFeatureFlagKey, partnerId: string, testVersion?: string): Promise<boolean> {
    const cacheKey = `${key}_${partnerId}`;
    if (this.featureFlagCache.has(cacheKey)) {
      return this.featureFlagCache.get(cacheKey) ?? false;
    }

    let url = `${this.mlaApiUrl}api/v1/configuration/keyvalue?key=${key}&partnerId=${partnerId}`;
    if (testVersion) {
      url += `&testVersion=${testVersion}`;
    }

    try {
      const response = await firstValueFrom(this.http.get<MlaFeatureFlagResponse>(url));

      if (response?.success && response.data) {
        const value =
          typeof response.data.value === 'boolean'
            ? response.data.value
            : String(response.data.value).toLowerCase() === 'true';

        this.featureFlagCache.set(cacheKey, value);
        return value;
      }

      return false;
    } catch (error) {
      this.loggingService.logException(error, `MlaConfigService: Error fetching feature flag ${key}`);
      return false;
    }
  }

  getFeatureFlag$(key: MlaFeatureFlagKey, partnerId: string, testVersion?: string): Observable<boolean> {
    let params = new HttpParams().set('key', key).set('partnerId', partnerId);

    if (testVersion) {
      params = params.set('testVersion', testVersion);
    }

    const url = `${this.mlaApiUrl}api/v1/configuration/keyvalue`;

    return this.http.get<MlaFeatureFlagResponse>(url, { params }).pipe(
      map((response) => {
        if (response?.success && response.data) {
          return typeof response.data.value === 'boolean'
            ? response.data.value
            : String(response.data.value).toLowerCase() === 'true';
        }
        return false;
      }),
      tap((value) => {
        this.featureFlagCache.set(`${key}_${partnerId}`, value);
      }),
      catchError((error) => {
        this.loggingService.logException(error, `MlaConfigService: Error fetching feature flag ${key}`);
        return of(false);
      })
    );
  }

  async isB2CAuthEnabled(partnerId: string): Promise<boolean> {
    const enabled = await this.getFeatureFlag('ADB2C_Enabled', partnerId);
    this.isB2CEnabled.set(enabled);
    return enabled;
  }

  async getEntraSettings(partnerId: string): Promise<MlaEntraSettings | null> {
    if (this.entraSettings()) {
      return this.entraSettings();
    }

    const url = `${this.mlaApiUrl}api/v1/configuration/setting?key=EntraSettings&partnerId=${partnerId}`;

    try {
      const response = await firstValueFrom(this.http.get<MlaConfigSettingResponse>(url));

      if (response?.success && response.data?.value) {
        const settings = JSON.parse(response.data.value) as MlaEntraSettings;
        this.entraSettings.set(settings);
        return settings;
      }

      return null;
    } catch (error) {
      this.loggingService.logException(error, 'MlaConfigService: Error fetching Entra settings');
      return null;
    }
  }

  async getB2CConfig(partnerId: string): Promise<MlaB2CConfigSettings | null> {
    const url = `${this.mlaApiUrl}api/v1/configuration/setting?key=B2CSettings&partnerId=${partnerId}`;

    try {
      const response = await firstValueFrom(this.http.get<MlaConfigSettingResponse>(url));

      if (response?.success && response.data?.value) {
        const config = JSON.parse(response.data.value) as MlaB2CConfigSettings;
        this.b2cConfig.set(config);
        return config;
      }

      return null;
    } catch (error) {
      this.loggingService.logException(error, 'MlaConfigService: Error fetching B2C config');
      return null;
    }
  }

  getB2CConfig$(partnerId: string): Observable<MlaB2CConfigSettings | null> {
    const params = new HttpParams().set('key', 'B2CSettings').set('partnerId', partnerId);

    const url = `${this.mlaApiUrl}api/v1/configuration/setting`;

    return this.http.get<MlaConfigSettingResponse>(url, { params }).pipe(
      map((response) => {
        if (response?.success && response.data?.value) {
          return JSON.parse(response.data.value) as MlaB2CConfigSettings;
        }
        return null;
      }),
      tap((config) => {
        if (config) {
          this.b2cConfig.set(config);
        }
      }),
      catchError((error) => {
        this.loggingService.logException(error, 'MlaConfigService: Error fetching B2C config');
        return of(null);
      })
    );
  }

  async getConfigSetting(key: string, partnerId: string): Promise<string | null> {
    const url = `${this.mlaApiUrl}api/v1/configuration/setting?key=${key}&partnerId=${partnerId}`;

    try {
      const response = await firstValueFrom(this.http.get<MlaConfigSettingResponse>(url));

      return response?.success ? response.data?.value : null;
    } catch (error) {
      this.loggingService.logException(error, `MlaConfigService: Error fetching config setting ${key}`);
      return null;
    }
  }

  async getHomeUrl(partnerId: string): Promise<void> {
    const url = await this.getConfigSetting('HomeUrl', partnerId);
    this.homePageUrl.set(url);
  }

  async isHelocEnabled(partnerId: string): Promise<boolean> {
    return this.getFeatureFlag('HeLoan_Enabled', partnerId);
  }

  async isPhoneConfirmationRequired(partnerId: string): Promise<boolean> {
    return this.getFeatureFlag('RegisterConfirmPhoneNumber', partnerId);
  }

  clearCache(): void {
    this.featureFlagCache.clear();
    this.entraSettings.set(null);
  }

  private googleMapsApiKey: string | null = null;

  async getGoogleMapsApiKey(partnerId: string): Promise<string | null> {
    if (this.googleMapsApiKey) {
      return this.googleMapsApiKey;
    }

    const url = `${this.mlaApiUrl}api/v1/configuration/keyvalue?key=Agm_Api_Key&partnerId=${partnerId}`;

    try {
      const response = await firstValueFrom(this.http.get<{ success: boolean; data: string }>(url));

      if (response?.success && response.data) {
        this.googleMapsApiKey = response.data;
        return this.googleMapsApiKey;
      }

      this.loggingService.logWarning('MlaConfigService: Get Google Maps API key was not successful', { success: String(response?.success) });
      return null;
    } catch (error) {
      this.loggingService.logException(error, 'MlaConfigService: Error fetching Google Maps API key');
      return null;
    }
  }
}
